import { useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { supabase } from "@/integrations/supabase/client";
import { CheckCircle2, AlertCircle, XCircle, Activity } from "lucide-react";

type Gate = {
  id: string;
  label: string;
  query: () => Promise<{ value: number; ok: boolean; detail?: string }>;
  threshold: string;
  area: "tom" | "persona" | "wm" | "people" | "skill" | "media" | "ops";
};

type GateResult = { value: number; ok: boolean; detail?: string };

const AREA_LABELS: Record<Gate["area"], string> = {
  tom: "ToM v2",
  persona: "Persona v19",
  wm: "Working Memory v18",
  people: "People Memory v19",
  skill: "Skill OS v19",
  media: "Multimodal v19",
  ops: "Operações",
};

async function countSince(table: string, hours: number): Promise<number> {
  const since = new Date(Date.now() - hours * 3600_000).toISOString();
  const { count } = await supabase
    .from(table as never)
    .select("*", { count: "exact", head: true })
    .gte("created_at", since);
  return count ?? 0;
}

const GATES: Gate[] = [
  {
    id: "tom_inferences_24h",
    label: "ToM turn inferences/24h",
    threshold: "≥ 20",
    area: "tom",
    query: async () => {
      const v = await countSince("tom_turn_inferences", 24);
      return { value: v, ok: v >= 20, detail: `${v} inferências nas últimas 24h` };
    },
  },
  {
    id: "composer_traces_24h",
    label: "Prompt composition traces/24h",
    threshold: "≥ 20",
    area: "persona",
    query: async () => {
      const v = await countSince("syra_prompt_composition_traces", 24);
      return { value: v, ok: v >= 20 };
    },
  },
  {
    id: "wm_24h",
    label: "Working memory writes/24h",
    threshold: "≥ 20",
    area: "wm",
    query: async () => {
      const v = await countSince("syra_working_memory", 24);
      return { value: v, ok: v >= 20 };
    },
  },
  {
    id: "wm_threads_total",
    label: "Threads ativas (total)",
    threshold: "≥ 1",
    area: "wm",
    query: async () => {
      const { count } = await supabase
        .from("syra_threads")
        .select("*", { count: "exact", head: true });
      const v = count ?? 0;
      return { value: v, ok: v >= 1, detail: "Pluga thOpenOrTouch (S50)" };
    },
  },
  {
    id: "people_audit_7d",
    label: "Person memory audit/7d",
    threshold: "≥ 20",
    area: "people",
    query: async () => {
      const v = await countSince("syra_person_memory_audit", 24 * 7);
      return { value: v, ok: v >= 20 };
    },
  },
  {
    id: "skill_v19_7d",
    label: "Skill executions v19/7d",
    threshold: "≥ 30",
    area: "skill",
    query: async () => {
      const v = await countSince("syra_skill_executions_v19", 24 * 7);
      return { value: v, ok: v >= 30 };
    },
  },
  {
    id: "media_items_24h",
    label: "Media items v19/24h",
    threshold: "≥ 1",
    area: "media",
    query: async () => {
      const v = await countSince("syra_media_items", 24);
      return { value: v, ok: v >= 1 };
    },
  },
  {
    id: "alerts_critical_14d",
    label: "Alertas críticos/14d",
    threshold: "= 0",
    area: "ops",
    query: async () => {
      const since = new Date(Date.now() - 14 * 24 * 3600_000).toISOString();
      const { count } = await supabase
        .from("alert_dispatches")
        .select("*", { count: "exact", head: true })
        .in("priority", ["critical", "high"])
        .gte("created_at", since);
      const v = count ?? 0;
      return { value: v, ok: v === 0 };
    },
  },
  {
    id: "drift_events_7d",
    label: "Persona drift events/7d",
    threshold: "= 0 (saudável)",
    area: "persona",
    query: async () => {
      const v = await countSince("syra_persona_drift_events", 24 * 7);
      return { value: v, ok: v === 0 };
    },
  },
];

export default function Phase3Readiness() {
  const [results, setResults] = useState<Record<string, GateResult | null>>({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      setLoading(true);
      const out: Record<string, GateResult | null> = {};
      await Promise.all(
        GATES.map(async (g) => {
          try {
            out[g.id] = await g.query();
          } catch (e) {
            out[g.id] = { value: -1, ok: false, detail: (e as Error).message };
          }
        }),
      );
      setResults(out);
      setLoading(false);
    })();
  }, []);

  const totalOk = Object.values(results).filter((r) => r?.ok).length;
  const totalChecked = Object.values(results).length;
  const allGreen = !loading && totalChecked === GATES.length && totalOk === totalChecked;

  return (
    <div className="container mx-auto p-6 space-y-6">
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Phase 3 GA Readiness</h1>
          <p className="text-sm text-muted-foreground">
            Scorecard operacional do closeout — S50→S55. Atualiza a cada visita.
          </p>
        </div>
        <Badge
          variant={allGreen ? "default" : loading ? "secondary" : "outline"}
          className="text-sm"
        >
          <Activity className="w-4 h-4 mr-1" />
          {loading ? "Avaliando..." : `${totalOk}/${totalChecked} gates verdes`}
        </Badge>
      </header>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {GATES.map((g) => {
          const r = results[g.id];
          return (
            <Card key={g.id} className="border">
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-sm font-medium">{g.label}</CardTitle>
                  {loading || !r ? (
                    <Skeleton className="h-4 w-4 rounded-full" />
                  ) : r.ok ? (
                    <CheckCircle2 className="w-5 h-5 text-primary" />
                  ) : r.value < 0 ? (
                    <XCircle className="w-5 h-5 text-destructive" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-muted-foreground" />
                  )}
                </div>
                <Badge variant="secondary" className="w-fit text-xs">
                  {AREA_LABELS[g.area]}
                </Badge>
              </CardHeader>
              <CardContent>
                {loading || !r ? (
                  <Skeleton className="h-8 w-24" />
                ) : (
                  <>
                    <div className="text-2xl font-semibold tabular-nums">
                      {r.value < 0 ? "—" : r.value}
                    </div>
                    <div className="text-xs text-muted-foreground mt-1">
                      Meta: {g.threshold}
                    </div>
                    {r.detail && (
                      <div className="text-xs text-muted-foreground mt-2">{r.detail}</div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          );
        })}
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Status do closeout</CardTitle>
        </CardHeader>
        <CardContent className="text-sm space-y-2">
          <p>
            <strong>S50</strong> — flags divergentes padronizadas, thread writer plugado.
          </p>
          <p>
            <strong>S51-S53</strong> — alcançados por inércia operacional (Fase 3 já em GA 100%).
          </p>
          <p>
            <strong>S54</strong> — handlers multimodais v19 em GA, flags fantasmas marcadas como
            DEPRECATED.
          </p>
          <p>
            <strong>S55</strong> — este dashboard. People memory crons em GA, sem alertas críticos
            14d.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}
